import type { editor } from 'monaco-editor'

import { Transition } from '@headlessui/react'
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import Editor, { EditorProps, loader, Monaco as MonacoType } from '@monaco-editor/react'
import ms from 'ms'
import { useRef, useState } from 'react'

import cn from '@/utilities/cn'

import BeautifyIcon from './BeautifyIcon'

loader.init()

export * from '@monaco-editor/react'

const defaultOptions: editor.IStandaloneEditorConstructionOptions = {
  tabSize: 2,
  automaticLayout: true,
  minimap: { enabled: false },
  autoIndent: 'full',
  formatOnPaste: true,
  formatOnType: true
}

export default function Monaco({
  showCopyToClipboardButton = true,
  showBeautifyButton = false,
  ...props
}: EditorProps & { showCopyToClipboardButton?: boolean; showBeautifyButton?: boolean }) {
  const editorRef = useRef<any>()

  const handleOnMount = (editor: any, monaco: MonacoType) => {
    editorRef.current = editor

    // extra libraries
    const libSource = `
      interface Inputs {
        [key: string]: any
      }
      interface State {
        [key: string]: any
      }
      interface Options {
        url: string | URL;
        method?: string;
        headers?: HeadersInit;
        body?: BodyInit;
        form?: FormData | ConstructorParameters<typeof URLSearchParams>[0] | string;
        json?: Object;
        searchParams?: ConstructorParameters<typeof URLSearchParams>[0];
        timeout?: number | false;
        retry?: { limit?: number; methods?: string[]; statusCodes?: number[] } | number;
        responseType?: 'text' | 'json';
        responsePath?: string;
      }

	    interface Client {
	      fetch: typeof fetch
	      request: (options: Options): any
	    };

	    declare const inputs: Inputs;
	    declare const state: State;
	    declare const client: Client;

	    // Modules
	    declare module 'npm:lodash@4' {}
	  `

    const libUri = `ts:filename/performEditor.d.ts`
    monaco.languages.typescript.typescriptDefaults.addExtraLib(libSource, libUri)

    // validation settings
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false
    })

    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      allowNonTsExtensions: true, // adding this option otherwise an error is thrown "Uncaught Error: Could not find source file"
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      typeRoots: ['node_modules/@types'],
      alwaysStrict: true,
      noUnusedParameters: true,
      noImplicitUseStrict: true,
      noUnusedLocals: true,
      target: monaco.languages.typescript.ScriptTarget.ESNext,
      lib: ['esnext', 'dom'],
      strict: false
    })
    setTimeout(async () => {
      await formatDocument()
    }, 50)

    return props.onMount?.(editor, monaco)
  }

  const formatDocument = async () => await editorRef.current.getAction('editor.action.formatDocument').run()

  return (
    <div className="relative h-full">
      {showBeautifyButton && (
        <BeautifyButton className="absolute top-0.5 right-6 z-10 flex flex-col items-end" onClick={formatDocument} />
      )}
      {showCopyToClipboardButton && (
        <CopyToClipboardButton
          className={cn('absolute top-0.5 right-6 z-10', { 'right-20': showBeautifyButton })}
          getText={() => editorRef.current?.getValue()}
        />
      )}

      <Editor {...props} options={{ ...defaultOptions, ...props.options }} onMount={handleOnMount} />
    </div>
  )
}

type ShowBeautifyButtonProps = {
  className?: string
  onClick: () => void
}

function BeautifyButton(props: ShowBeautifyButtonProps) {
  const [clicked, setClicked] = useState(false)

  const handleClick = () => {
    props.onClick()
    setClicked(true)
    setTimeout(() => setClicked(false), ms('4 seconds'))
  }

  return (
    <div className={props.className}>
      <button
        className="z-10 mx-1 flex rounded-md bg-white stroke-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:cursor-pointer hover:bg-gray-50 hover:stroke-gray-400"
        onClick={handleClick}
      >
        <BeautifyIcon className="mx-3 my-2 h-5 w-5" />
      </button>
      <Transition
        show={clicked}
        enter="transition-opacity duration-125"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className={`mr-1 select-none text-sm font-thin text-gray-400`}>Its so Beautiful!</div>
      </Transition>
    </div>
  )
}

type CopyToClipboardButtonProps = {
  getText: () => string
  className?: string
}

function CopyToClipboardButton(props: CopyToClipboardButtonProps) {
  const [copied, setCopied] = useState(false)

  const handleClick = () => {
    navigator.clipboard.writeText(props.getText())
    setCopied(true)
    setTimeout(() => setCopied(false), ms('4 seconds'))
  }

  return (
    <div className={props.className}>
      <button
        className="z-10 mx-1 flex rounded-md bg-white stroke-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:cursor-pointer hover:bg-gray-50 hover:stroke-gray-400"
        onClick={handleClick}
      >
        <ClipboardDocumentIcon className="mx-3 my-2 h-5 w-5" />
      </button>
      <Transition
        show={copied}
        enter="transition-opacity duration-125"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className={`mr-1 select-none text-sm font-thin text-gray-400`}>Copied!</div>
      </Transition>
    </div>
  )
}
